import { Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { useNotifications } from "@toolpad/core";
import { useEffect, useState } from "react";
import ConfigModal from "../../components/ConfigModal";
import CustomTable from "../../components/CustomTable";
import ModalWindow from "../../components/modal/ModalWindow";
import {
  fetchConfigs,
  sendTestEmail,
  sendTestSms,
} from "../../services/adminService";
import { handleApiError } from "../../utils/errorHandler";

export default function AdminConfigPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [shouldReload, setShouldReload] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testPhone, setTestPhone] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const notifications = useNotifications();

  useEffect(() => {
    const getConfigs = async () => {
      setError("");

      const result = await fetchConfigs();

      if (result.success) {
        setConfigs(result.data);
      } else {
        setError(handleApiError(result));
      }
      setLoading(false);
    };

    getConfigs();
  }, [shouldReload]);

  const handleRowClick = (row) => {
    setSelectedConfig(row);
    setModalVisible(true);
  };

  const handleAddConfig = () => {
    setSelectedConfig(null);
    setModalVisible(true);
  };

  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      notifications.show("Zadejte e-mail příjemce.", {
        severity: "warning",
        autoHideDuration: 5000,
      });
      return;
    }

    setSendingEmail(true);
    const result = await sendTestEmail(testEmail.trim());
    setSendingEmail(false);

    if (result.success) {
      notifications.show(result.data?.message || "Testovací e-mail byl odeslán.", {
        severity: "success",
        autoHideDuration: 5000,
      });
      return;
    }

    notifications.show(result.message || "Chyba při odesílání testovacího e-mailu.", {
      severity: "error",
      autoHideDuration: 5000,
    });
  };

  const handleSendTestSms = async () => {
    if (!testPhone.trim()) {
      notifications.show("Zadejte telefonní číslo.", {
        severity: "warning",
        autoHideDuration: 5000,
      });
      return;
    }

    setSendingSms(true);
    const result = await sendTestSms(testPhone.trim());
    setSendingSms(false);

    if (result.success) {
      notifications.show(result.data?.message || "Testovací SMS byla odeslána.", {
        severity: "success",
        autoHideDuration: 5000,
      });
      return;
    }

    notifications.show(result.message || "Chyba při odesílání testovací SMS.", {
      severity: "error",
      autoHideDuration: 5000,
    });
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", p: 2 }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Načítání...
        </Typography>
      </Box>
    );
  }

  if (error) {
    throw new Response(error.message, { status: error.status });
  }

  const configColumns = [
    {
      id: "nazev",
      label: "Název",
    },
    {
      id: "hodnota",
      label: "Hodnota",
    },
  ];

  return (
    <>
      <Box sx={{ margin: "0 auto", textAlign: "center", p: 2 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Konfigurace
        </Typography>

        <Button variant="contained" sx={{ mb: 2 }} onClick={handleAddConfig}>
          Přidat klíč
        </Button>

        <CustomTable
          columns={configColumns}
          data={configs}
          onRowClick={handleRowClick}
          getRowKey={(row) => row.id || Math.random()}
          pagination={false}
        />

        <Box
          sx={{
            mt: 4,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxWidth: 500,
            margin: "0 auto",
          }}
        >
          <Typography variant="h6">Test doručení</Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              label="E-mail příjemce"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleSendTestEmail}
              disabled={sendingEmail}
            >
              Odeslat test e-mail
            </Button>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              label="Telefon"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={handleSendTestSms}
              disabled={sendingSms}
            >
              Odeslat test SMS
            </Button>
          </Box>
        </Box>
      </Box>
      <ModalWindow visible={modalVisible} setVisible={setModalVisible}>
        <ConfigModal
          key={selectedConfig?.id || Math.random()}
          setModalVisible={setModalVisible}
          selectedConfig={selectedConfig}
          setShouldReload={setShouldReload}
        />
      </ModalWindow>
    </>
  );
}
